import { StarIcon, TagIcon } from "@heroicons/react/24/solid";
import { Author, Tag, FavouriteObjectType } from "../../types/content";
import CardWithImageTopAndFooter from "./CardWithImageTopAndFooter";
import ConsumptionButton from "./ConsumptionButton";
import RatingModal from "./RatingModal";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { getReviewForContent, selectRating, submitReviewRating } from "../../slices/review";
import { addAlert } from "../../slices/alert";

export default function PostCard({
  objectId,
  objectType,
  image,
  bgColour = "",
  href,
  title,
  authors,
  primaryTags,
  synopsis,
  consumption,
  user,
  rating,
  reviews,
  price = "",
  locked = false,
}: {
  objectId: string;
  objectType: FavouriteObjectType;
  image: string;
  bgColour?: string;
  href?: string | null;
  title: string;
  authors: Author[];
  primaryTags: Tag[];
  synopsis: string;
  consumption: any[];
  user: any;
  rating: number;
  reviews: number;
  price: string;
  /**
   * Members-only content this visitor cannot open. The card still renders as a
   * teaser and every route into the content asks them to sign in. Comes from
   * the API payload, which withholds the href for a locked item.
   */
  locked?: boolean;
}) {
  const dispatch = useAppDispatch();

  // Scoped to this object. A single shared slot meant rating one card wrote its
  // score onto every other mounted card.
  const { score, rating: liveRating, reviews: liveReviews, loading } = useAppSelector(
    (state) => selectRating(state, objectId)
  );

  // Prefer the freshly fetched aggregate over the value baked into the list
  // payload -- that payload is served from a cache with a ~24h TTL, so right after
  // rating it still reports the old average.
  const displayRating = liveReviews > 0 ? liveRating : rating;
  const displayReviews = liveReviews > 0 ? liveReviews : reviews;

  useEffect(() => {
    if (loading) return;
    if (score === null) return;
    setStarRatingValue(score);
  }, [
    loading,
    score
  ]);

  const [showStarRatingModal, setShowStarRatingModal] = useState(false);
  const [starRatingText, setStarRatingText] = useState("");
  const [starRatingId, setStarRatingId] = useState("");
  const [starRatingValue, setStarRatingValue] = useState(0);

  async function triggerSubmitRating(e: any) {
    e.preventDefault();
    if (starRatingValue < 1) {
      dispatch(addAlert("Pick a star rating before submitting.", "orange"));
      return;
    }
    const res: any = await dispatch(submitReviewRating({
      objectId: starRatingId,
      objectType: objectType,
      score: starRatingValue
    }))
    if (res.payload?.status === 200) {
      setStarRatingValue(0);
      setStarRatingText("");
      setStarRatingId("");
      setShowStarRatingModal(false);
      dispatch(addAlert("Rating submitted, thanks!", "mint-green"));
    } else {
      // The thunk rejects with the server's reason string; a silent no-op here
      // looked identical to a rating that saved but did not refresh.
      const reason = typeof res.payload === "string" ? res.payload : "";
      dispatch(addAlert(reason || "Could not submit your rating. Please try again.", "orange"));
    }
  }

  return (
    <>
      <RatingModal
        open={showStarRatingModal}
        onClose={setShowStarRatingModal}
        title={starRatingText}
        value={starRatingValue}
        onChange={setStarRatingValue}
        onSubmit={triggerSubmitRating}
      />

      <CardWithImageTopAndFooter
        objectId={objectId}
        objectType={objectType}
        image={image}
        bgColour={bgColour}
        href={href}
        openOnCardClick={false}
        locked={locked}
        dataTitle={title}
        dataAuthor={authors.map((author: Author) => author.name).join(", ")}
        dataTags={primaryTags.map((tag: any) => tag.name).join(", ")}
        middleContent={
          <>
            {/* Title */}
            <h1
              className="text-sm font-bold line-clamp-2 overflow-hidden text-ellipsis break-words text-dark-blue"
              title={title} // This sets the full text as the tooltip on hover
            >
              {title}
            </h1>

            {/* Author */}
            {authors.length > 0 && (
              <p title={title} className="text-sm font-medium line-clamp-1 overflow-hidden text-ellipsis break-words text-dark-blue">{authors.map((x: any) => x.name).join(", ")}</p>
            )}

            {/* Collection */}
            {/* {collections.length > 0 && (
              <p className="text-sm font-medium">{collections[0].name}</p>
            )} */}

            {/* Tags */}
            {/* <div className="flex flex-wrap gap-[0.1rem]">
              {primaryTags.map((tag: Tag, index: number) => {
                return (
                  <span key={`tag-${index}`} className="flex items-center bg-transparent border border-dark-blue text-dark-blue px-1.5 py-0.2 rounded-full text-xxs font-bold">{tag.name}</span>
                );
              })}
            </div> */}
            <div className="flex flex-wrap gap-[0.1rem] overflow-hidden max-h-[1.2rem]">
              {primaryTags.map((tag: Tag, index: number) => (
                <span
                  key={`tag-${index}`}
                  className="flex items-center bg-transparent border border-dark-blue text-dark-blue px-1.5 py-0.2 rounded-full text-xxs font-bold"
                >
                  {tag.name}
                </span>
              ))}
            </div>

            {/* Synopsis */}
            <p title={synopsis} className="text-xs line-clamp-4 overflow-hidden text-ellipsis break-words text-dark-blue">{synopsis}</p>
          </>
        }
        footerContent={(openContent, opensInNewTab) => (
          <>
            {/* Consumption — the card's only way through to the content */}
            {consumption.length > 0 && (
              <ConsumptionButton
                consumption={consumption[0].name}
                title={title}
                onClick={openContent}
                opensInNewTab={opensInNewTab}
                locked={locked}
              />
            )}

            {/* Rating */}
            <div
              className={`group flex items-center gap-1 cursor-pointer`}
              onClick={(e) => {
                e.stopPropagation();
                if (user !== null) {
                  setStarRatingText(title);
                  setStarRatingId(objectId);
                  setStarRatingValue(0);
                  setShowStarRatingModal(true);
                  dispatch(getReviewForContent({
                    contentId: objectId,
                    contentType: objectType
                  }));
                }
              }}
            >
              <StarIcon className={`shrink-0 h-5 w-5 text-${user !== null ? "yellow" : "secondary"}`} />
              {user !== null ? (
                <>
                  <span className="text-sm font-bold text-dark-blue">{displayRating}</span>
                  <span className="text-sm font-bold text-dark-blue">({displayReviews})</span>
                </>
              ) : (
                <span
                  className="
                relative bottom-14 mt-1 -translate-x-1/2
                px-2 py-1 bg-black text-white text-xs rounded
                opacity-0 group-hover:opacity-100 pointer-events-none z-50
              "
                >
                  You must be logged in to see the ratings!
                </span>
              )}
            </div>

            {/* Price */}
            {price && (
              <div className="flex items-center gap-1 text-dark-blue">
                <TagIcon className="h-5 w-5 shrink-0" />
                <span className="text-sm font-bold">{price}</span>
              </div>
            )}
          </>
        )}
      />
    </>
  );
}
